import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core';
import {
  AnalyticsAdapter,
  AnalyticsConfig,
  AnalyticsProperties,
} from './analytics.types';

interface MixpanelClient {
  init(
    projectKey: string,
    config: {
      autocapture: false;
      track_pageview: false;
      record_sessions_percent: 0;
    },
  ): void;
  track(event: string, properties?: AnalyticsProperties): void;
  identify(userId: string): void;
  register(properties: AnalyticsProperties): void;
  reset(): void;
}

/**
 * Internal Mixpanel implementation. Keep this adapter private to the library so
 * applications depend only on the provider-neutral analytics facade.
 */
export class MixpanelAnalyticsAdapter implements AnalyticsAdapter {
  private sdkInitialized = false;
  private initializationAttempted = false;
  private initialContextRegistered = false;

  constructor(
    private readonly config: AnalyticsConfig,
    private readonly client: MixpanelClient = mixpanel,
  ) {}

  public track(event: string, properties?: AnalyticsProperties): void {
    this.withMixpanel(() => this.client.track(event, properties));
  }

  public identify(userId: string): void {
    this.withMixpanel(() => this.client.identify(userId));
  }

  public setContext(properties: AnalyticsProperties): void {
    this.withMixpanel(() => this.client.register(properties));
  }

  public reset(): void {
    if (!this.ensureSdkInitialized()) {
      return;
    }

    try {
      this.client.reset();
    } catch {
      // Analytics failures must never affect the host application.
      return;
    }

    // reset() clears all registered properties. Only restore static context;
    // organization context must be re-established by its owner after logout.
    this.initialContextRegistered = !this.config.initialContext;
    this.ensureInitialContextRegistered();
  }

  private withMixpanel(operation: () => void): void {
    if (!this.ensureReady()) {
      return;
    }

    try {
      operation();
    } catch {
      // Analytics failures must never affect the host application.
    }
  }

  private ensureReady(): boolean {
    return (
      this.ensureSdkInitialized() && this.ensureInitialContextRegistered()
    );
  }

  private ensureSdkInitialized(): boolean {
    if (!this.config.enabled || !this.config.projectKey) {
      return false;
    }

    if (this.sdkInitialized) {
      return true;
    }

    if (this.initializationAttempted) {
      return false;
    }

    this.initializationAttempted = true;

    try {
      this.client.init(this.config.projectKey, {
        autocapture: false,
        track_pageview: false,
        record_sessions_percent: 0,
      });
      this.sdkInitialized = true;
      return true;
    } catch {
      return false;
    }
  }

  private ensureInitialContextRegistered(): boolean {
    if (this.initialContextRegistered) {
      return true;
    }

    if (!this.config.initialContext) {
      this.initialContextRegistered = true;
      return true;
    }

    try {
      this.client.register(this.config.initialContext);
      this.initialContextRegistered = true;
      return true;
    } catch {
      return false;
    }
  }
}
